import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import UserProfileIcon from '../components/UserProfileIcon';
import {
  IELTSExamDuration,
  IELTSListeningSession,
  IELTSListeningSection,
  IELTSListeningQuestion,
  IELTSListeningEvaluation,
} from '../types/ielts';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5555';

interface LocationState {
  duration: IELTSExamDuration;
}

const IELTSListeningPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuth();
  const audioRef = useRef<HTMLAudioElement>(null);

  const duration = (location.state as LocationState)?.duration || 'short';

  const getSectionCount = (dur: IELTSExamDuration): number => {
    switch (dur) {
      case 'short': return 1;
      case 'medium': return 2;
      case 'full': return 4;
      default: return 1;
    }
  };

  const [session, setSession] = useState<IELTSListeningSession>({
    examType: 'listening',
    duration,
    currentSection: 0,
    sections: [],
    answers: [],
    status: 'loading',
  });

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [audioDuration, setAudioDuration] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loadingMessage, setLoadingMessage] = useState('Generating listening exam...');

  // Generate listening exam on mount
  useEffect(() => {
    const generateExam = async () => {
      try {
        setLoadingMessage('Generating listening content...');
        const sectionCount = getSectionCount(duration);

        const response = await fetch(`${API_URL}/api/listening/generate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ sectionCount }),
        });

        if (!response.ok) {
          throw new Error('Failed to generate listening exam');
        }

        const data = await response.json();

        // Validate response structure - server returns { exam: { sections }, audioData }
        const examSections = data?.exam?.sections;
        const audioData = data?.audioData;

        // Fallback: Check if response might have sections at top level (legacy format)
        const rawSections = examSections || data?.sections;

        if (!rawSections || !Array.isArray(rawSections) || rawSections.length === 0) {
          console.error('Invalid response structure:', data);
          throw new Error('Invalid response: No sections received from server');
        }

        // Transform sections to match client-side type expectations
        const transformedSections: IELTSListeningSection[] = rawSections.map((section: any, index: number) => {
          // Find matching audio data for this section
          const sectionAudio = audioData?.find(
            (audio: any) => audio.sectionNumber === section.sectionNumber
          );

          // Convert base64 audio to data URL if available
          let audioUrl: string | undefined;
          if (sectionAudio?.audioBase64) {
            audioUrl = `data:audio/mp3;base64,${sectionAudio.audioBase64}`;
          }

          // Generate transcript from script lines if available
          let transcript: string | undefined;
          if (section.script && Array.isArray(section.script)) {
            transcript = section.script
              .map((line: any) => {
                const speakerName = section.speakers?.find((s: any) => s.id === line.speaker)?.name || line.speaker;
                return `${speakerName}: ${line.text}`;
              })
              .join('\n\n');
          }

          // Validate questions exist and have required fields
          const questions = (section.questions || []).map((q: any, qIndex: number) => ({
            id: q.id || `section-${section.sectionNumber}-q-${qIndex}`,
            type: q.type || 'short_answer',
            question: q.question || '',
            options: q.options,
            correctAnswer: q.correctAnswer || '',
            explanation: q.explanation,
          }));

          if (questions.length === 0) {
            console.warn(`Section ${section.sectionNumber} has no questions`);
          }

          return {
            id: section.id || `section-${section.sectionNumber}`,
            sectionNumber: section.sectionNumber || (index + 1),
            title: section.title || `Section ${section.sectionNumber || index + 1}`,
            description: section.description || section.context || '',
            audioUrl,
            transcript,
            questions,
            timeMinutes: section.durationSeconds ? Math.ceil(section.durationSeconds / 60) : 10,
          };
        });

        // Final validation: ensure we have valid sections with questions
        const validSections = transformedSections.filter(s => s.questions.length > 0);

        if (validSections.length === 0) {
          console.error('No valid sections with questions found');
          throw new Error('Invalid response: Sections have no questions');
        }

        setSession(prev => ({
          ...prev,
          sections: validSections,
          status: 'in-progress',
        }));
      } catch (err) {
        console.error('Error generating exam:', err);
        const errorMessage = err instanceof Error ? err.message : 'Unknown error';
        setError(`Failed to generate listening exam: ${errorMessage}. Please try again.`);
      }
    };

    generateExam();
  }, [duration]);

  // Block copy/paste/cut during the test
  useEffect(() => {
    const preventCopyPaste = (e: ClipboardEvent) => {
      if (session.status === 'in-progress') {
        e.preventDefault();
        return false;
      }
    };

    const preventContextMenu = (e: MouseEvent) => {
      if (session.status === 'in-progress') {
        e.preventDefault();
        return false;
      }
    };

    document.addEventListener('copy', preventCopyPaste);
    document.addEventListener('paste', preventCopyPaste);
    document.addEventListener('cut', preventCopyPaste);
    document.addEventListener('contextmenu', preventContextMenu);

    return () => {
      document.removeEventListener('copy', preventCopyPaste);
      document.removeEventListener('paste', preventCopyPaste);
      document.removeEventListener('cut', preventCopyPaste);
      document.removeEventListener('contextmenu', preventContextMenu);
    };
  }, [session.status]);

  const handleLogout = async () => {
    try {
      await logout();
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  const currentSection = session.sections?.[session.currentSection];

  const handleAnswerChange = (questionId: string, answer: string) => {
    setSession(prev => {
      const existingIndex = prev.answers.findIndex(a => a.questionId === questionId);
      const newAnswers = [...prev.answers];

      if (existingIndex >= 0) {
        newAnswers[existingIndex] = { questionId, answer };
      } else {
        newAnswers.push({ questionId, answer });
      }

      return { ...prev, answers: newAnswers };
    });
  };

  const getAnswer = (questionId: string): string => {
    return session.answers.find(a => a.questionId === questionId)?.answer as string || '';
  };

  const handlePlayPause = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setAudioDuration(audioRef.current.duration);
    }
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const time = parseFloat(e.target.value);
    if (audioRef.current) {
      audioRef.current.currentTime = time;
      setCurrentTime(time);
    }
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleNextSection = () => {
    if (session.currentSection < session.sections.length - 1) {
      setSession(prev => ({
        ...prev,
        currentSection: prev.currentSection + 1,
      }));
      setIsPlaying(false);
      setCurrentTime(0);
    } else {
      // Complete exam
      calculateResults();
    }
  };

  const handlePrevSection = () => {
    if (session.currentSection > 0) {
      setSession(prev => ({
        ...prev,
        currentSection: prev.currentSection - 1,
      }));
      setIsPlaying(false);
      setCurrentTime(0);
    }
  };

  const calculateResults = () => {
    let correctCount = 0;
    let totalQuestions = 0;

    session.sections.forEach(section => {
      section.questions.forEach(question => {
        totalQuestions++;
        const userAnswer = getAnswer(question.id)?.toLowerCase().trim();
        const correctAnswer = Array.isArray(question.correctAnswer)
          ? question.correctAnswer.map(a => a.toLowerCase().trim())
          : [question.correctAnswer.toLowerCase().trim()];

        if (correctAnswer.includes(userAnswer)) {
          correctCount++;
        }
      });
    });

    const percentageCorrect = totalQuestions > 0 ? (correctCount / totalQuestions) * 100 : 0;

    // Calculate band score (approximate)
    let bandScore = 0;
    if (percentageCorrect >= 90) bandScore = 9;
    else if (percentageCorrect >= 80) bandScore = 8;
    else if (percentageCorrect >= 70) bandScore = 7;
    else if (percentageCorrect >= 60) bandScore = 6.5;
    else if (percentageCorrect >= 50) bandScore = 6;
    else if (percentageCorrect >= 40) bandScore = 5.5;
    else if (percentageCorrect >= 30) bandScore = 5;
    else if (percentageCorrect >= 20) bandScore = 4;
    else bandScore = 3;

    const evaluation: IELTSListeningEvaluation = {
      overallBand: bandScore,
      correctAnswers: correctCount,
      totalQuestions,
      percentageCorrect,
      feedback: `You answered ${correctCount} out of ${totalQuestions} questions correctly.`,
      strengths: percentageCorrect >= 60 ? ['Good listening comprehension'] : [],
      improvements: percentageCorrect < 60 ? ['Practice listening to various accents', 'Focus on keywords and context'] : [],
    };

    setSession(prev => ({
      ...prev,
      evaluation,
      status: 'completed',
    }));
  };

  const renderQuestion = (question: IELTSListeningQuestion, index: number) => {
    switch (question.type) {
      case 'multiple_choice':
        return (
          <div key={question.id} className="mb-6 p-4 bg-white dark:bg-gray-800 rounded-lg shadow-sm dark:shadow-gray-900">
            <p className="font-medium text-gray-800 dark:text-white mb-3">
              {index + 1}. {question.question}
            </p>
            <div className="space-y-2">
              {question.options?.map((option, i) => (
                <label key={i} className="flex items-center gap-3 p-2 rounded hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer">
                  <input
                    type="radio"
                    name={question.id}
                    value={option}
                    checked={getAnswer(question.id) === option}
                    onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                    className="w-4 h-4 text-emerald-600"
                  />
                  <span className="text-gray-700 dark:text-gray-200">{option}</span>
                </label>
              ))}
            </div>
          </div>
        );

      case 'fill_in_blanks':
      case 'short_answer':
        return (
          <div key={question.id} className="mb-6 p-4 bg-white dark:bg-gray-800 rounded-lg shadow-sm dark:shadow-gray-900">
            <p className="font-medium text-gray-800 dark:text-white mb-3">
              {index + 1}. {question.question}
            </p>
            <input
              type="text"
              value={getAnswer(question.id)}
              onChange={(e) => handleAnswerChange(question.id, e.target.value)}
              className="w-full p-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
              placeholder="Type your answer..."
            />
          </div>
        );

      default:
        return (
          <div key={question.id} className="mb-6 p-4 bg-white dark:bg-gray-800 rounded-lg shadow-sm dark:shadow-gray-900">
            <p className="font-medium text-gray-800 dark:text-white mb-3">
              {index + 1}. {question.question}
            </p>
            <input
              type="text"
              value={getAnswer(question.id)}
              onChange={(e) => handleAnswerChange(question.id, e.target.value)}
              className="w-full p-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
              placeholder="Type your answer..."
            />
          </div>
        );
    }
  };

  // Loading state
  if (session.status === 'loading') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white dark:from-gray-900 dark:to-gray-800 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-300 text-lg">{loadingMessage}</p>
          <p className="text-gray-400 dark:text-gray-500 text-sm mt-2">This may take a minute...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white dark:from-gray-900 dark:to-gray-800 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="w-16 h-16 bg-red-100 dark:bg-red-900/50 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-800 dark:text-white mb-2">Something went wrong</h2>
          <p className="text-gray-600 dark:text-gray-300 mb-6">{error}</p>
          <button
            onClick={() => navigate('/app/ielts')}
            className="px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-medium"
          >
            Back to IELTS
          </button>
        </div>
      </div>
    );
  }

  // Completed state
  if (session.status === 'completed' && session.evaluation) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white dark:from-gray-900 dark:to-gray-800">
        <div className="max-w-3xl mx-auto px-4 py-8">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg dark:shadow-gray-900 p-8 text-center">
            <div className="w-20 h-20 bg-emerald-100 dark:bg-emerald-900/50 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-emerald-600 dark:text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>

            <h1 className="text-3xl font-bold text-gray-800 dark:text-white mb-2">Listening Test Complete!</h1>
            <p className="text-gray-600 dark:text-gray-300 mb-8">Here are your results</p>

            <div className="grid grid-cols-2 gap-6 mb-8">
              <div className="bg-purple-50 dark:bg-purple-900/30 rounded-xl p-6">
                <p className="text-sm text-purple-600 dark:text-purple-300 font-medium mb-1">Band Score</p>
                <p className="text-4xl font-bold text-purple-700 dark:text-purple-400">{session.evaluation.overallBand}</p>
              </div>
              <div className="bg-emerald-50 dark:bg-emerald-900/30 rounded-xl p-6">
                <p className="text-sm text-emerald-600 dark:text-emerald-300 font-medium mb-1">Correct Answers</p>
                <p className="text-4xl font-bold text-emerald-700 dark:text-emerald-400">
                  {session.evaluation.correctAnswers}/{session.evaluation.totalQuestions}
                </p>
              </div>
            </div>

            <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-6 mb-8 text-left">
              <h3 className="font-semibold text-gray-800 dark:text-white mb-3">Feedback</h3>
              <p className="text-gray-600 dark:text-gray-300">{session.evaluation.feedback}</p>

              {session.evaluation.strengths.length > 0 && (
                <div className="mt-4">
                  <h4 className="font-medium text-emerald-700 dark:text-emerald-400 mb-2">Strengths</h4>
                  <ul className="space-y-1">
                    {session.evaluation.strengths.map((s, i) => (
                      <li key={i} className="text-gray-600 dark:text-gray-300 flex items-center gap-2">
                        <span className="text-emerald-500">+</span> {s}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {session.evaluation.improvements.length > 0 && (
                <div className="mt-4">
                  <h4 className="font-medium text-orange-700 dark:text-orange-400 mb-2">Areas to Improve</h4>
                  <ul className="space-y-1">
                    {session.evaluation.improvements.map((s, i) => (
                      <li key={i} className="text-gray-600 dark:text-gray-300 flex items-center gap-2">
                        <span className="text-orange-500">!</span> {s}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <div className="flex gap-4 justify-center">
              <button
                onClick={() => navigate('/app/ielts')}
                className="px-6 py-3 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Back to IELTS
              </button>
              <button
                onClick={() => window.location.reload()}
                className="px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-medium"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Main exam view
  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white dark:from-gray-900 dark:to-gray-800">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow-sm dark:shadow-gray-900 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/app/ielts')}
              className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 flex items-center gap-1 p-2 -m-2"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              <span className="hidden sm:inline">Back</span>
            </button>
            <span className="text-gray-300 dark:text-gray-600">|</span>
            <span className="text-sm font-medium text-purple-600 dark:text-purple-400">
              IELTS Listening - Section {session.currentSection + 1} of {session.sections.length}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleLogout}
              className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
            >
              Logout
            </button>
            <UserProfileIcon />
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6">
        {currentSection && (
          <>
            {/* Section Info */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm dark:shadow-gray-900 p-6 mb-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-xl font-bold text-gray-800 dark:text-white">
                    Section {currentSection.sectionNumber}: {currentSection.title}
                  </h2>
                  <p className="text-gray-600 dark:text-gray-300 mt-1">{currentSection.description}</p>
                </div>
                <div className="text-right">
                  <span className="text-sm text-gray-500 dark:text-gray-400">Questions</span>
                  <p className="text-lg font-semibold text-purple-600 dark:text-purple-400">{currentSection.questions.length}</p>
                </div>
              </div>

              {/* Audio Player */}
              {currentSection.audioUrl && (
                <div className="bg-purple-50 dark:bg-purple-900/30 rounded-xl p-4 mt-4">
                  <audio
                    ref={audioRef}
                    src={currentSection.audioUrl}
                    onTimeUpdate={handleTimeUpdate}
                    onLoadedMetadata={handleLoadedMetadata}
                    onEnded={() => setIsPlaying(false)}
                  />
                  <div className="flex items-center gap-4">
                    <button
                      onClick={handlePlayPause}
                      className="w-12 h-12 bg-purple-600 hover:bg-purple-700 rounded-full flex items-center justify-center text-white"
                    >
                      {isPlaying ? (
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      ) : (
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      )}
                    </button>
                    <div className="flex-1">
                      <input
                        type="range"
                        min="0"
                        max={audioDuration || 100}
                        value={currentTime}
                        onChange={handleSeek}
                        className="w-full h-2 bg-purple-200 rounded-lg appearance-none cursor-pointer"
                      />
                      <div className="flex justify-between text-sm text-purple-700 dark:text-purple-300 mt-1">
                        <span>{formatTime(currentTime)}</span>
                        <span>{formatTime(audioDuration)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* No audio fallback - show transcript */}
              {!currentSection.audioUrl && currentSection.transcript && (
                <div className="bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-700 rounded-xl p-4 mt-4">
                  <p className="text-sm text-yellow-700 dark:text-yellow-300 mb-2 font-medium">Audio not available - Read the transcript:</p>
                  <p className="text-gray-700 dark:text-gray-200 whitespace-pre-wrap">{currentSection.transcript}</p>
                </div>
              )}
            </div>

            {/* Questions */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">Questions</h3>
              {currentSection.questions.map((question, index) => renderQuestion(question, index))}
            </div>

            {/* Navigation */}
            <div className="flex justify-between items-center">
              <button
                onClick={handlePrevSection}
                disabled={session.currentSection === 0}
                className={`px-6 py-3 rounded-lg flex items-center gap-2 ${
                  session.currentSection === 0
                    ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                    : 'bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200'
                }`}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Previous
              </button>

              <button
                onClick={handleNextSection}
                className="px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg flex items-center gap-2"
              >
                {session.currentSection === session.sections.length - 1 ? 'Submit' : 'Next Section'}
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default IELTSListeningPage;
